/**
 * Custom hook for request tracking and cancellation
 * Integrates with RequestTracker singleton for DVA model request management
 */

import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import RequestTracker from '@/utils/infrastructure/communication/requestTracker';

/**
 * Hook for managing request tracking and cancellation for a namespace
 * @param {string} namespace - DVA model namespace to track
 * @param {Object} options - Configuration options
 * @param {boolean} [options.cancelOnUnmount=true] - Cancel all requests on unmount
 * @param {boolean} [options.enabled=true] - Enable/disable tracking for this namespace
 * @returns {Object} Request tracker methods
 * @returns {Function} returns.cancelAll - Cancel all active requests for namespace
 * @returns {Function} returns.cancelRequest - Cancel specific request by ID
 * @returns {Function} returns.getActiveCount - Get count of active requests
 * @returns {Function} returns.getActiveRequests - Get array of active request info
 * @returns {boolean} returns.isTracking - Whether tracking is enabled
 */
function useRequestTracker(namespace, options = {}) {
  const { cancelOnUnmount = true, enabled = true } = options;

  // Store namespace in ref to access in cleanup without dependency issues
  const namespaceRef = useRef(namespace);
  const cancelOnUnmountRef = useRef(cancelOnUnmount);

  useEffect(() => {
    namespaceRef.current = namespace;
    cancelOnUnmountRef.current = cancelOnUnmount;
  }, [namespace, cancelOnUnmount]);

  useEffect(() => {
    const tracker = RequestTracker.getInstance();
    tracker.setEnabled(namespace, enabled);

    return () => {
      // Reset to default enabled state on unmount if we disabled it
      if (!enabled) {
        tracker.setEnabled(namespace, true);
      }
    };
  }, [namespace, enabled]);

  useEffect(() => {
    return () => {
      if (cancelOnUnmountRef.current) {
        const tracker = RequestTracker.getInstance();
        try {
          tracker.cancelAll(namespaceRef.current);
        } catch {}
      }
    };
  }, []);

  const cancelAll = useCallback(() => {
    const tracker = RequestTracker.getInstance();
    return tracker.cancelAll(namespace);
  }, [namespace]);

  const cancelRequest = useCallback(
    (requestId) => {
      const tracker = RequestTracker.getInstance();
      return tracker.cancelRequest(namespace, requestId);
    },
    [namespace],
  );

  const getActiveCount = useCallback(() => {
    const tracker = RequestTracker.getInstance();
    return tracker.getActiveCount(namespace);
  }, [namespace]);

  const getActiveRequests = useCallback(() => {
    const tracker = RequestTracker.getInstance();
    return tracker.getActiveRequests(namespace);
  }, [namespace]);

  const isTracking = useMemo(() => {
    const tracker = RequestTracker.getInstance();
    return tracker.isEnabled(namespace);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namespace, enabled]);

  return useMemo(
    () => ({
      cancelAll,
      cancelRequest,
      getActiveCount,
      getActiveRequests,
      isTracking,
    }),
    [cancelAll, cancelRequest, getActiveCount, getActiveRequests, isTracking],
  );
}

/**
 * HOC for class components to access request tracker
 * @param {string} namespace - DVA model namespace to track
 * @param {Object} options - Configuration options
 * @param {boolean} [options.cancelOnUnmount=true] - Cancel all requests on unmount
 * @param {boolean} [options.enabled=true] - Enable/disable tracking for this namespace
 * @returns {Function} Higher-order component
 */
function withRequestTracker(namespace, options = {}) {
  return function (WrappedComponent) {
    const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

    function WithRequestTracker(props) {
      const tracker = useRequestTracker(namespace, options);

      return React.createElement(WrappedComponent, {
        ...props,
        requestTracker: tracker,
      });
    }

    WithRequestTracker.displayName = `withRequestTracker(${displayName})`;
    WithRequestTracker.WrappedComponent = WrappedComponent;

    return WithRequestTracker;
  };
}

export { useRequestTracker, withRequestTracker };
export default useRequestTracker;
